import React, { useState, useContext, useEffect } from "react";
import api from "axios_interceptor/interceptor";
import {
  Text,
  Flex,
  Heading,
  Button,
  TextField,
  Select,
  AlertDialog,
  Badge,
  Tooltip,
  Spinner,
} from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { UserInfoContext } from "context/UserInfoContext";
import { QueueViewContext } from "context/QueueViewContext";
import { RandomTopicContext } from "context/RandomTopicContext";

const GenerateView = () => {
  const [topic, setTopic] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [len, setlen] = useState("short");
  const [voice, setVoice] = useState("af_sky");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { currentUserInfo, setCurrentUserInfo } = useContext(UserInfoContext);
  const { setCurrentQueueView } = useContext(QueueViewContext);
  const { randomTopics, setRandomTopics } = useContext(RandomTopicContext);

  const handleGenerateClick = () => {
    setIsAlertOpen(true);
  };

  const fetchRandomTopics = async () => {
    if (randomTopics.length === 0) {
      try {
        const response = await api.post("/utils/generate-topics");
        const data = response.data.content;
        setRandomTopics(data);
        setLoadingTopics(false);
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    }
    setLoadingTopics(false);
  };

  const fetchAudioStatus = async () => {
    try {
      const response = await api.post("/utils/audio-check-by-user");
      const audioQueue = response.data.content;
      setCurrentQueueView(audioQueue);
    } catch (error) {
      console.error("Error fetching audio status:", error);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get("/auth/user-info");
      const data = response.data;
      if (data.content.length !== 0) setCurrentUserInfo(data.content[0]);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const handleConfirmClick = () => {
    const data = {
      length: len,
      voice: voice,
      topic: topic,
    };

    setIsLoading(true);

    api
      .post("/utils/audio-create", data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (response.data?.code === 200 && response.data?.content?.message) {
          // Display alert with the message from the response
          setIsErrorAlertOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        fetchAudioStatus();
        fetchUserData();
        setIsLoading(false);
        setIsAlertOpen(false); // Close alert dialog
      });
  };

  useEffect(() => {
    fetchRandomTopics();
  }, []);

  return (
    <div>
      {isLoading && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999,
            }}
          />

          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            <Spinner />
          </div>
        </>
      )}

      <Flex align="center" justify="between" mb="5">
        <Flex gap="2">
          <Heading as="h3" size="4" trim="both">
            Create audio
          </Heading>
          <Tooltip content="Tokens are used to create audio, 1 token equals 1 audio">
            <Badge color="orange" style={{ cursor: "pointer" }}>
              Tokens: {currentUserInfo.count}
            </Badge>
          </Tooltip>
        </Flex>

        <Flex my="-1" gap="4">
          <Button
            size="2"
            variant="soft"
            onClick={handleGenerateClick}
            color="green"
            style={{ cursor: "pointer" }}
          >
            Generate
          </Button>
        </Flex>
      </Flex>

      <Flex direction="column" gap="4">
        <Flex asChild direction="column" gap="1">
          <Text as="label" size="2" weight="medium">
            <Flex gap="1">
              <Text>Topic</Text>
              <Tooltip content="Enter a phrase or a word on the topic you want to explore.">
                <InfoCircledIcon
                  style={{ marginTop: "2px", cursor: "pointer" }}
                />
              </Tooltip>
            </Flex>

            <Flex direction="column" gap="2">
              <TextField.Root
                variant="classic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Explanation of Plato's Allegory of the Cave"
              />

              <Flex direction="row">
                {loadingTopics ? (
                  <Spinner />
                ) : (
                  randomTopics.map((topic, index) => (
                    <Badge
                      key={index}
                      color="gold"
                      onClick={() => setTopic(topic)}
                      style={{ cursor: "pointer", marginRight: "5px" }}
                    >
                      {topic}
                    </Badge>
                  ))
                )}
              </Flex>
            </Flex>
          </Text>
        </Flex>

        <Flex asChild direction="column" gap="1">
          <Text as="label" size="2" weight="medium">
            <Flex gap="1">
              <Text>Format</Text>
              <Tooltip content="Summary is 5 minutes long and takes 5 minutes to generate. Podcast offers a more in-depth format and is currently in beta. It is 30 minutes long and requires 30 minutes to generate.">
                <InfoCircledIcon
                  style={{ marginTop: "2px", cursor: "pointer" }}
                />
              </Tooltip>
            </Flex>

            <Select.Root
              defaultValue={len}
              onValueChange={(value) => setlen(value)}
            >
              <Select.Trigger style={{ cursor: "pointer" }} />
              <Select.Content>
                <Select.Group>
                  <Select.Label>Format</Select.Label>
                  <Select.Item value="short" style={{ cursor: "pointer" }}>
                    Summary
                  </Select.Item>
                  <Select.Item value="long" style={{ cursor: "pointer" }}>
                    Podcast ⚠️ Experimental – Currently in Beta⚠️{" "}
                  </Select.Item>
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Text>
        </Flex>

        <Flex asChild direction="column" gap="1">
          <Text as="label" size="2" weight="medium">
            <Flex gap="1">
              <Text>Voice</Text>
              <Tooltip content="Select your preferred voice.">
                <InfoCircledIcon
                  style={{ marginTop: "2px", cursor: "pointer" }}
                />
              </Tooltip>
            </Flex>

            <Select.Root
              defaultValue={voice}
              onValueChange={(value) => setVoice(value)}
            >
              <Select.Trigger style={{ cursor: "pointer" }} />
              <Select.Content>
                <Select.Group>
                  <Select.Label>Voice</Select.Label>
                  <Select.Item value="af_sky" style={{ cursor: "pointer" }}>
                    Jessica (Female){" "}
                  </Select.Item>
                  <Select.Item value="af_heart" style={{ cursor: "pointer" }}>
                    Murphy (Female)
                  </Select.Item>
                  <Select.Item value="af_nicole" style={{ cursor: "pointer" }}>
                    Amelia 👩‍🚀 (ASMR Female)
                  </Select.Item>
                  <Select.Item value="am_adam" style={{ cursor: "pointer" }}>
                    Leto (Male)
                  </Select.Item>
                  <Select.Item value="am_liam" style={{ cursor: "pointer" }}>
                    Cooper (Male)
                  </Select.Item>
                  <Select.Item value="am_santa" style={{ cursor: "pointer" }}>
                    Albus 🧙‍♂️ (Male)
                  </Select.Item>
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Text>
        </Flex>
      </Flex>

      <AlertDialog.Root open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Generate audio</AlertDialog.Title>

          {!topic.trim() || topic.trim().length <= 1 ? (
            <>
              <AlertDialog.Description size="2">
                Topic cannot be empty or have a single letter 🫡
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel asChild>
                  <Button
                    variant="soft"
                    color="gray"
                    style={{ cursor: "pointer" }}
                    onClick={() => setIsAlertOpen(false)}
                  >
                    Cancel
                  </Button>
                </AlertDialog.Cancel>
              </Flex>
            </>
          ) : (
            <>
              <AlertDialog.Description size="2">
                Are you sure you want to generate the audio with the given
                settings? You will use{" "}
                <Badge color="orange" style={{ cursor: "pointer" }}>
                  1
                </Badge>{" "}
                token.
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel asChild>
                  <Button
                    variant="soft"
                    color="gray"
                    style={{ cursor: "pointer" }}
                    onClick={() => setIsAlertOpen(false)}
                  >
                    Cancel
                  </Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <Button
                    variant="solid"
                    color="green"
                    style={{ cursor: "pointer" }}
                    onClick={handleConfirmClick}
                  >
                    Generate
                  </Button>
                </AlertDialog.Action>
              </Flex>
            </>
          )}
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root
        open={isErrorAlertOpen}
        onOpenChange={setIsErrorAlertOpen}
      >
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Error</AlertDialog.Title>
          <AlertDialog.Description size="2">
            You cannot create more audio, please get more tokens.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel asChild>
              <Button
                variant="soft"
                color="red"
                style={{ cursor: "pointer" }}
                onClick={() => setIsAlertOpen(false)}
              >
                Ok
              </Button>
            </AlertDialog.Cancel>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </div>
  );
};

export default GenerateView;
